import React from "react";
import { Link } from "@/lib/router-compat";
import { Phone, Mail, Facebook } from "lucide-react";
import { CEMFLOOR_LOGO } from "./Navbar";
import { LazyImg } from "./LazyImg";
import { trackCall, trackEmail, trackWhatsApp } from "@/lib/track";

export const Footer: React.FC = () => {
  return (
    <footer className="bg-[#11100E] text-stone-300 border-t border-stone-800/80 pt-16 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-12 border-b border-stone-800">
          {/* Company Brand Column */}
          <div className="md:col-span-6 space-y-4">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="inline-block"
              aria-label="Exeter Flow Screeds - Home"
            >
              <img
                src="https://assets.cdn.filesafe.space/iehMW4z3GZgoPjqmxz5p/media/6ac5174922bc27f58f99e396.webp"
                alt="Exeter Flow Screeds"
                width={288}
                height={112}
                loading="lazy"
                decoding="async"
                className="h-28 sm:h-36 w-auto object-contain"
              />
            </a>
            <p className="text-stone-400 text-sm max-w-md leading-relaxed">
              Fast, level, perfect floors every time. We supply and install
              liquid screed and underfloor heating floors across Exeter and the
              South West.
            </p>

            <div className="pt-2 flex items-center gap-3">
              <LazyImg
                src={CEMFLOOR_LOGO}
                alt="Cemfloor Certified"
                width={104}
                height={42}
                loading="lazy"
                decoding="async"
                className="h-6 w-auto object-contain opacity-90"
              />
              <span className="text-xs text-stone-400 font-medium">
                Cemfloor Certified Specialists
              </span>
            </div>
            <div className="flex items-center gap-4 pt-2">
              <a
                href="https://www.facebook.com/profile.php?id=61578220532150"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="text-stone-400 hover:text-[#C9A84C] transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="md:col-span-3 space-y-3">
            <h4 className="text-white font-bold text-sm tracking-wider uppercase">
              Quick Links
            </h4>
            <ul className="space-y-2 text-sm text-stone-400">
              <li>
                <Link to="/" className="hover:text-[#C9A84C] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/projects"
                  className="hover:text-[#C9A84C] transition-colors font-semibold text-stone-300"
                >
                  Our Projects
                </Link>
              </li>
              <li>
                <Link
                  to="/#what-we-do"
                  className="hover:text-[#C9A84C] transition-colors"
                >
                  Services
                </Link>
              </li>
              <li>
                <a
                  href="/#service-area"
                  className="hover:text-[#C9A84C] transition-colors"
                >
                  Service Area
                </a>
              </li>
              <li>
                <a
                  href="/#contact"
                  className="hover:text-[#C9A84C] transition-colors"
                >
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Direct Contact Column */}
          <div className="md:col-span-3 space-y-3">
            <h4 className="text-white font-bold text-sm tracking-wider uppercase">
              Direct Contact
            </h4>
            <ul className="space-y-3 text-sm text-stone-400">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#C9A84C]" />
                <a
                  href="tel:077657126609"
                  onClick={() => trackCall("07765 712609", "footer")}
                  className="hover:text-[#C9A84C] transition-colors font-semibold text-stone-200"
                >
                  07765 712609
                </a>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#C9A84C]" />
                <a
                  href="tel:07470861869"
                  onClick={() => trackCall("07470 861869", "footer")}
                  className="hover:text-[#C9A84C] transition-colors font-semibold text-stone-200"
                >
                  07470 861869
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="w-4 h-4 text-[#C9A84C] shrink-0 mt-0.5" />
                <a
                  href="mailto:efscreed@gmail.com"
                  onClick={() => trackEmail("efscreed@gmail.com", "footer")}
                  className="hover:text-[#C9A84C] transition-colors break-all text-xs sm:text-sm"
                >
                  efscreed@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright & Legal Links */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-stone-500 gap-4">
          <div className="flex flex-wrap items-center gap-3 text-stone-400">
            <p>
              © {new Date().getFullYear()} Exeter Flow Screeds Ltd. All rights
              reserved.
            </p>
            <span className="hidden sm:inline text-stone-700">•</span>
            <Link
              to="/privacy-policy"
              className="hover:text-[#C9A84C] transition-colors"
            >
              Privacy Policy
            </Link>
            <span className="text-stone-700">•</span>
            <Link
              to="/terms-and-conditions"
              className="hover:text-[#C9A84C] transition-colors"
            >
              Terms & Conditions
            </Link>
          </div>
          <p className="flex items-center gap-1 text-[#11100E]">
            <span className="text-[#11100E]">Powered by</span>
            <a
              href="https://liftlocaldigital.com/"
              target="_blank"
              rel="noreferrer"
              className="text-[#11100E] hover:text-[#11100E] transition-colors font-medium underline"
            >
              LiftLocal Digital
            </a>
          </p>
        </div>
      </div>

      {/* Floating WhatsApp Action Button */}
      <a
        href="https://api.whatsapp.com/send/?phone=%2B447765712609&text=Hi%2C+I+have+a+question+about+liquid+screeding...&type=phone_number&app_absent=0"
        target="_blank"
        rel="noreferrer"
        aria-label="Contact us on WhatsApp"
        onClick={() => trackWhatsApp("floating_button")}
        className="fixed bottom-6 right-6 z-40 bg-[#25D366] hover:bg-[#20ba5a] text-white p-3.5 rounded-full shadow-2xl transition-all duration-300 hover:scale-110 flex items-center justify-center group"
      >
        <svg
          className="w-7 h-7 fill-white"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        <span className="max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-xs transition-all duration-300 group-hover:ml-2 font-bold text-sm">
          WhatsApp Us
        </span>
      </a>
    </footer>
  );
};
